import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import i18n from '@/i18n';
import AppLayout from '@/layout/AppLayout';
import LeftNavLayout from '@/layout/LeftNavLayout';
import { lazyNamedImport } from '@/utils/lazyNamedImport';

import ErrorBoundary from '../ErrorBoundary';

const PlatMembers = lazy(() => import('@/pages/system/security-center/users'));
const PlatRoles = lazy(() => import('@/pages/system/security-center/roles'));
const Tenant = lazy(() => import('@/pages/system/security-center/tenants'));
const TenantDetail = lazy(() => import('@/pages/system/security-center/tenants/detail'));
const MenuConf = lazy(() => import('@/pages/system/settings/menu-settings'));
const Audit = lazyNamedImport(() => import('@/pages/system/security-center/audit'), 'Audit');
const DataCenters = lazy(() => import('@/pages/system/resources/data-centers'));
const DataCenterDetail = lazy(() => import('@/pages/system/resources/data-centers/detail'));
const LargeScreen = lazyNamedImport(() => import('@/pages/system/global-view/large-screen'), 'LargeScreen');
const EditScreenData = lazyNamedImport(() => import('@/pages/system/global-view/large-screen/edit/edit-screen-data'), 'EditScreenData');
const ProductCatalogue = lazyNamedImport(() => import('@/pages/system/ops-center/service-catalog'), 'ProductCatalogue');
const ProductCatalogueDetail = lazyNamedImport(() => import('@/pages/system/ops-center/service-catalog/detail'), 'ProductCatalogueDetail');
const Account = lazy(() => import('@/pages/system/ops-center/ops-policy'));
const Recharge = lazyNamedImport(() => import('@/pages/system/ops-center/recharge-apply'), 'Recharge');
const NoticeList = lazyNamedImport(() => import('@/pages/system/ops-center/notices'), 'NoticeList');
const CouponStrategyPage = lazyNamedImport(() => import('@/pages/system/ops-center/coupon-strategy'), 'CouponStrategyPage');
const CouponStrategyCreatePage = lazyNamedImport(
  () => import('@/pages/system/ops-center/coupon-strategy/create'),
  'CouponStrategyCreatePage',
);
const CouponStrategyDetailPage = lazyNamedImport(
  () => import('@/pages/system/ops-center/coupon-strategy/detail'),
  'CouponStrategyDetailPage',
);
const CarouselPage = lazyNamedImport(() => import('@/pages/system/ops-center/carousels'), 'CarouselPage');
const CarouselDetail = lazyNamedImport(() => import('@/pages/system/ops-center/carousels/detail'), 'CarouselDetail');
const ProductType = lazyNamedImport(() => import('@/pages/system/ops-center/product-types'), 'ProductType');
const ProductTypeDetail = lazyNamedImport(() => import('@/pages/system/ops-center/product-types/detail'), 'ProductTypeDetail');
const Product = lazyNamedImport(() => import('@/pages/system/ops-center/product'), 'Product');
const ProductDetail = lazyNamedImport(() => import('@/pages/system/ops-center/product/detail'), 'ProductDetail');
const ProductCreate = lazyNamedImport(() => import('@/pages/system/ops-center/product/create'), 'ProductCreate');
const LoginSettings = lazy(() => import('@/pages/system/settings/login-settings'));
// 报表中心
const ReportOverview = lazy(() => import('@/pages/report-center/overview'));
const ReportCompute = lazy(() => import('@/pages/report-center/compute'));
const ReportModel = lazy(() => import('@/pages/report-center/model'));
const ReportApp = lazy(() => import('@/pages/report-center/app'));
const ReportOps = lazy(() => import('@/pages/report-center/ops'));
const ReportBusiness = lazy(() => import('@/pages/report-center/business'));
const ReportResource = lazy(() => import('@/pages/report-center/resource'));
const t = i18n.t.bind(i18n);

export const systemRouters = {
  path: '/',
  element: <AppLayout />,
  title: t('common:system.settings'),
  children: [
    {
      element: <LeftNavLayout />,
      errorElement: <ErrorBoundary />,
      children: [
        { title: 'largeScreen', path: '/system/large-screen', element: <LargeScreen /> },
        {
          path: '/system/large-screen/edit',
          element: <EditScreenData />,
        },
        {
          title: 'tenant',
          path: '/system/tenants',
          element: <Tenant />,
        },
        {
          title: 'dataCenterManagement',
          path: '/system/datacenters',
          element: <DataCenters />,
        },
        {
          title: 'dataCenterDetail',
          path: '/system/datacenters/:name/detail',
          element: <DataCenterDetail />,
        },
        {
          title: 'platform-user',
          path: '/system/users',
          element: <PlatMembers />,
        },
        {
          title: 'platform-roles',
          path: '/system/roles',
          element: <PlatRoles />,
        },
        {
          path: '/system/tenants/:name/detail',
          element: <TenantDetail />,
        },
        {
          path: '/system/product-catalogue',
          element: <ProductCatalogue />,
        },
        {
          path: '/system/account',
          element: <Account />,
        },
        {
          path: '/system/recharge',
          element: <Recharge />,
        },
        {
          path: '/system/product-catalogue/:id',
          element: <ProductCatalogueDetail />,
        },
        {
          title: 'systemMenus',
          path: '/system/menus',
          element: <MenuConf />,
        },
        { title: 'audit', path: '/system/audit', element: <Audit /> },
        {
          title: 'notice',
          path: '/system/notice',
          element: <NoticeList />,
        },
        {
          title: 'couponStrategy',
          path: '/system/coupon-strategy',
          element: <CouponStrategyPage />,
        },
        {
          path: '/system/coupon-strategy/create',
          element: <CouponStrategyCreatePage />,
        },
        {
          path: '/system/coupon-strategy/:id/edit',
          element: <CouponStrategyCreatePage />,
        },
        {
          path: '/system/coupon-strategy/:id',
          element: <CouponStrategyDetailPage />,
        },
        // 产品管理（轮播图 / 产品类别 / 产品列表）
        { title: 'carouselManagement', path: '/system/carousels', element: <CarouselPage /> },
        { path: '/system/carousels/:carouselId', element: <CarouselDetail /> },
        { title: 'productTypes', path: '/system/product-types', element: <ProductType /> },
        { path: '/system/product-types/:productTypeId', element: <ProductTypeDetail /> },
        { title: 'productList', path: '/system/products', element: <Product /> },
        { path: '/system/products/create', element: <ProductCreate /> },
        { path: '/system/products/:productId', element: <ProductDetail /> },
        { path: '/system/products/:productId/edit', element: <ProductCreate /> },
        { path: '/system/product-types/:productTypeId/products/:productId', element: <ProductDetail /> },
        { path: '/system/product-types/:productTypeId/products/:productId/edit', element: <ProductCreate /> },
        {
          title: 'loginSettings',
          path: '/system/platform-settings',
          element: <LoginSettings />,
        },
        // 兼容旧书签 / 外链
        { path: '/system/login-settings', element: <Navigate to="/system/platform-settings" replace /> },
        // 报表中心
        { title: 'reportOverview', path: '/system/report-center/overview', element: <ReportOverview /> },
        { title: 'reportCompute', path: '/system/report-center/compute', element: <ReportCompute /> },
        { title: 'reportModel', path: '/system/report-center/model', element: <ReportModel /> },
        { title: 'reportApp', path: '/system/report-center/app', element: <ReportApp /> },
        { title: 'reportOps', path: '/system/report-center/ops', element: <ReportOps /> },
        { title: 'reportBusiness', path: '/system/report-center/business', element: <ReportBusiness /> },
        { title: 'reportResource', path: '/system/report-center/resource', element: <ReportResource /> },
      ],
    },
  ],
};
